import { useMemo, useState, useCallback } from "react"
import { Link } from "react-router-dom"
import { Button, Card, CardBody, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Badge } from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import { useAccounts } from "hooks/useAccountEntity"
import { useAccountTransactions, useAccountTransaction, useReverseAccountTransaction } from "hooks/useAccountTransaction"
import { formatCurrency } from "utils/formatCurrency"
import { formatReadableDate } from "utils/formatDate"
import AccountFilterPanel from "pages/AccountPages/utils/AccountFilterPanel"
import AccountTransactionView from "pages/AccountPages/AccountTransaction/AccountTransactionView"
import ReversalBadge from "pages/AccountPages/utils/ReversalBadge"
import { getActiveRecords, getReversalEntries, getRecordStatus, sumAmounts } from "pages/AccountPages/utils/reversalHelpers"
import WorkspaceStatCard from "../components/WorkspaceStatCard"

const LedgerTab = () => {
  const [filters, setFilters] = useState({ date_from: "", date_to: "", account_id: "", status: "", search: "" })
  const [viewModal, setViewModal] = useState(false)
  const [selectedViewId, setSelectedViewId] = useState(null)
  const [reverseModal, setReverseModal] = useState(false)
  const [selectedReverseItem, setSelectedReverseItem] = useState(null)

  const queryParams = useMemo(() => ({
    page: 1,
    per_page: 10,
    search: filters.search || "",
    ...(filters.date_from && { date_from: filters.date_from }),
    ...(filters.date_to && { date_to: filters.date_to }),
    ...(filters.account_id && { account_id: filters.account_id }),
  }), [filters])

  const { data: responseData, isLoading, refetch } = useAccountTransactions(queryParams)
  const { data: accountResponse } = useAccounts({ all: true, per_page: 200 })
  const { data: viewResponse, isLoading: viewLoading } = useAccountTransaction(selectedViewId, viewModal)
  const { mutate: reverseTransaction, isPending: reversing } = useReverseAccountTransaction()

  const accounts = accountResponse?.data?.data || accountResponse?.data || []
  const transactions = responseData?.data?.data || responseData?.data || []
  const filteredTransactions = useMemo(() => {
    if (!filters.status) return transactions
    return transactions.filter(item => getRecordStatus(item) === filters.status)
  }, [transactions, filters.status])

  const summary = useMemo(() => {
    const postedEntries = getActiveRecords(transactions)
    const reversalEntries = getReversalEntries(transactions)
    const netMovement = filteredTransactions.reduce((sum, item) => {
      const type = (item.transaction_type?.name || item.transactionType?.name || "").toLowerCase()
      return sum + (type.includes("credit") ? Number(item.amount || 0) : Number(item.amount || 0) * -1)
    }, 0)

    return {
      grossPosted: sumAmounts(postedEntries),
      reversalAdjustments: sumAmounts(reversalEntries),
      netMovement,
    }
  }, [transactions, filteredTransactions])

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [])

  const columns = useMemo(() => [
    { header: "Account", cell: ({ row }) => row.original.account?.name || "-" },
    {
      header: "Type",
      cell: ({ row }) => {
        const type = row.original.transaction_type?.name || row.original.transactionType?.name || "-"
        const isCredit = type.toLowerCase().includes("credit")
        return (
          <div className="d-flex gap-1 align-items-center">
            <Badge color={isCredit ? "success" : "danger"}>{type}</Badge>
            <ReversalBadge isReversal={row.original.is_reversal} isReversed={row.original.is_reversed} />
          </div>
        )
      },
    },
    { header: "Reference", cell: ({ row }) => row.original.reference_type || "-" },
    { header: "Date", cell: ({ row }) => row.original.date ? formatReadableDate(row.original.date) : "-" },
    {
      header: "Amount",
      cell: ({ row }) => {
        const type = row.original.transaction_type?.name || row.original.transactionType?.name || ""
        const isCredit = type.toLowerCase().includes("credit")
        return <span className={`fw-semibold ${isCredit ? "text-success" : "text-danger"}`}>{isCredit ? "+" : "-"}{formatCurrency(row.original.amount || 0, row.original.account?.currency || "BDT")}</span>
      },
    },
    {
      header: "Action",
      cell: ({ row }) => {
        const item = row.original
        const canReverse = !item.is_reversal && !item.is_reversed
        return (
          <div className="d-flex gap-2">
            <Button size="sm" color="primary" onClick={() => { setSelectedViewId(item.id); setViewModal(true) }}>View</Button>
            {canReverse && <Button size="sm" color="warning" onClick={() => { setSelectedReverseItem(item); setReverseModal(true) }}>Reverse</Button>}
          </div>
        )
      },
    },
  ], [])

  return (
    <Card className="border-0 shadow-sm">
      <CardBody>
        <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
          <div>
            <h5 className="mb-1">Ledger</h5>
            <p className="text-muted mb-0 small">Inspect the transaction stream and reverse entries without leaving the workspace.</p>
          </div>
          <div className="d-flex gap-2">
            <Button color="light" className="border" onClick={() => refetch()}>Refresh</Button>
            <Link to="/account-transaction-list" className="btn btn-light border">Open full ledger</Link>
          </div>
        </div>

        <Row className="g-3 mb-3">
          <Col md={4}>
            <WorkspaceStatCard title="Gross Posted" value={formatCurrency(summary.grossPosted, "BDT")} subtitle="Visible non-reversal entries" icon="mdi mdi-book-open-page-variant-outline" color="primary" />
          </Col>
          <Col md={4}>
            <WorkspaceStatCard title="Reversal Adjustments" value={formatCurrency(summary.reversalAdjustments, "BDT")} subtitle="Counter-entries in the current feed" icon="mdi mdi-swap-horizontal" color="info" />
          </Col>
          <Col md={4}>
            <WorkspaceStatCard title="Net Movement" value={formatCurrency(Math.abs(summary.netMovement), "BDT")} subtitle={summary.netMovement >= 0 ? "Net credit movement in the filtered feed" : "Net debit movement in the filtered feed"} icon={summary.netMovement >= 0 ? "mdi mdi-trending-up" : "mdi mdi-trending-down"} color={summary.netMovement >= 0 ? "success" : "danger"} />
          </Col>
        </Row>

        <AccountFilterPanel filters={filters} onChange={handleFilterChange} onReset={() => setFilters({ date_from: "", date_to: "", account_id: "", status: "", search: "" })}>
          <Col xs={12} sm={6} md={3}>
            <Label className="mb-1 small fw-semibold">Account</Label>
            <Input type="select" bsSize="sm" value={filters.account_id || ""} onChange={e => handleFilterChange("account_id", e.target.value)}>
              <option value="">All Accounts</option>
              {accounts.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
            </Input>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Label className="mb-1 small fw-semibold">Status</Label>
            <Input type="select" bsSize="sm" value={filters.status || ""} onChange={e => handleFilterChange("status", e.target.value)}>
              <option value="">All Entries</option>
              <option value="active">Posted</option>
              <option value="reversed">Reversed</option>
              <option value="reversal_entry">Reversal Entry</option>
            </Input>
          </Col>
        </AccountFilterPanel>

        <Row className="mb-3">
          <Col md={4}>
            <GlobalInputSearch value={filters.search} onChange={value => handleFilterChange("search", value)} />
          </Col>
        </Row>

        <TableContainer columns={columns} data={filteredTransactions} loading={isLoading} isPagination={false} />
      </CardBody>

      <AccountTransactionView modal={viewModal} toggleModal={() => { setViewModal(prev => !prev); if (viewModal) setSelectedViewId(null) }} transaction={viewResponse?.data} loading={viewLoading} />
      <Modal isOpen={reverseModal} toggle={() => setReverseModal(false)} centered size="sm">
        <ModalHeader toggle={() => setReverseModal(false)}>Reverse Transaction</ModalHeader>
        <ModalBody>
          <p className="mb-1">Post a counter-entry for this transaction?</p>
          {selectedReverseItem && <div className="text-muted small">{selectedReverseItem.reference_type} #{selectedReverseItem.reference_id} | {formatCurrency(selectedReverseItem.amount || 0, selectedReverseItem.account?.currency || "BDT")}</div>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setReverseModal(false)}>Cancel</Button>
          <Button
            color="warning"
            disabled={reversing}
            onClick={() => {
              if (!selectedReverseItem) return
              reverseTransaction(selectedReverseItem.id, {
                onSuccess: () => {
                  setReverseModal(false)
                  setSelectedReverseItem(null)
                  refetch()
                },
              })
            }}
          >
            {reversing ? "Reversing..." : "Confirm"}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  )
}

export default LedgerTab
